import {ESelect, TEnum, TRecord, TText} from '@packages/systems/wfdl-ast';

import {
  Component,
  Expressions,
  type ExpressionPath,
  matchExpression,
  Plugins,
  Variable,
  withMeta,
  withSystem,
} from '@packages/systems/wfdl';
import type {Expression} from '@packages/systems/wfdl-ast';
import {
  concat,
  concatTo,
  constant,
  constantFalse,
  emptyArray,
} from '@packages/utilities/fp/utils';
import {$} from '@packages/utilities/wfdl-helpers';
import {
  type UserAcessMetaOption,
  type UserAccessMeta,
  USYS_ACCESS_TYPES,
} from '@packages/systems/users/constants';
import {
  getPluginName,
  identifier,
  qualify,
  getName,
} from '@packages/systems/wfdl-Qualified';
import {weakMemo} from '@packages/utilities/memo';
import type {Qualified} from '@packages/systems/wfdl-Qualified';
import type {Pair} from '@packages/utilities/Pair';

type BoundUserSelectMap = {
  bindingValue: Expression;
  userAccessMetaOption: UserAcessMetaOption;
};

const isUserLoggedInSelectMap: BoundUserSelectMap = {
  bindingValue: ESelect({
    from: $('context'),
    prop: 'isUserLoggedIn',
  }),
  userAccessMetaOption: USYS_ACCESS_TYPES.LOGGED_IN,
};

// @ts-expect-error - TS7031 - Binding element 'fun' implicitly has an 'any' type. | TS7031 - Binding element 'arg' implicitly has an 'any' type.
const getAccessGroupsFromCall = ({fun, arg}) => {
  const callWithinACall = Expressions.getCall(fun);
  if (callWithinACall === null) {
    return Expressions.resume(emptyArray);
  }

  const matchingSelect = isIsInAccessGroupSelect(callWithinACall.fun);

  if (!matchingSelect) {
    return Expressions.resume(emptyArray);
  }

  const accessGroupShortId = Expressions.getEnum(arg);

  return Expressions.resume(
    accessGroupShortId ? [accessGroupShortId] : emptyArray
  );
};

const accessGroupSlugsMatcher = matchExpression({
  Call: getAccessGroupsFromCall,
  _: constant(Expressions.resume(emptyArray)),
});

export const getAccessGroupSlugsFromBind = (expr: Expression) =>
  Expressions.everything(concat)(accessGroupSlugsMatcher)(expr);

const isMatchingSelect = ({
  var2,
  prop2,
}: {
  var2: Variable.VariableType;
  prop2: string;
}) =>
  matchExpression({
    Select: ({from, prop}: {from: Expression; prop: string}) => {
      if (prop !== prop2) return false;
      const var1 = Expressions.getVariable(from);
      if (!var1 || !var2) return false;
      return Variable.equals(var1)(var2);
    },
    _: constantFalse,
  });

// List of ESelects used to select User-related variables with their corresponding userAccessMeta identifier
export const usersSelectMap: BoundUserSelectMap[] = [isUserLoggedInSelectMap];

// Reduces bindingValues to a list of userAccessMetaOption used to create x-wf-usys-variant: an identifier for the version of a page the node appears on.
// @ts-expect-error - TS7031 - Binding element 'from' implicitly has an 'any' type. | TS7031 - Binding element 'prop' implicitly has an 'any' type.
const getUserAccessMetaFromSelect = ({from, prop}): UserAccessMeta => {
  const variable = Expressions.getVariable(from);
  if (variable === null) return [];

  return usersSelectMap.reduce<Array<any>>(
    (userAccessMeta, {bindingValue, userAccessMetaOption}) => {
      if (isMatchingSelect({var2: variable, prop2: prop})(bindingValue)) {
        userAccessMeta.push(userAccessMetaOption);
      }
      return userAccessMeta;
    },
    []
  );
};

export const hasUsersBinding = (expr: Expression): boolean =>
  getUsysGateGroupAccess(expr).size > 0;

const usysGateGroupAccessMatcher = matchExpression({
  Select: (expr) => {
    const userAccessMetaCombined = getUserAccessMetaFromSelect(expr);
    if (userAccessMetaCombined) {
      return Expressions.stop(userAccessMetaCombined);
    }
    return Expressions.resume(emptyArray);
  },
  Element: constant(Expressions.stop(emptyArray)),
  _: constant(Expressions.resume(emptyArray)),
});

export const getUsysGateGroupAccess: (arg1: Expression) => Set<string> = (
  boundExpression
) =>
  new Set(
    Expressions.everything(concatTo)(usysGateGroupAccessMatcher)(
      boundExpression
    )
  );

export const usysFieldType = TRecord({
  fieldType: withSystem({private: true}, TText),
  context: withMeta(
    {variant: 'menu'},
    withSystem(
      {
        defaultValue: 'none',
        private: true,
      },
      TEnum({
        none: 'None',
        signUp: 'Sign Up Form',
        userAccount: 'User Account Form',
      })
    )
  ),
  field: withSystem({private: true}, TText),
});

export const findInComponentMapByComponentName = (
  componentMap: Component.ComponentMap,
  rootComponentName: Qualified<Component.ComponentName>,
  seenComponents: Set<string> = new Set()
): string[] => {
  const component = componentMap.get(rootComponentName);
  if (component === undefined) return [];

  return Expressions.everything(concat)(
    matchBoundAccessGroup(componentMap)(seenComponents)
  )(Component.getRender(component));
};

const isIsInAccessGroupSelect = isMatchingSelect({
  var2: Variable.unqualified(identifier('context')),
  prop2: 'isInAccessGroup',
});

export const matchBoundAccessGroup = weakMemo(
  (componentMap: Component.ComponentMap) => (seenComponents: Set<string>) =>
    matchExpression<Pair<string[], boolean>>({
      Element: (element) => {
        const {type} = element;
        if (getPluginName(type) === '__SitePlugin') {
          const componentName = getName(type);
          if (seenComponents.has(componentName)) {
            return Expressions.resume(emptyArray);
          } else {
            seenComponents.add(componentName);
            return Expressions.resume(
              findInComponentMapByComponentName(
                componentMap,
                type,
                seenComponents
              )
            );
          }
        }
        return Expressions.resume(emptyArray);
      },
      Call: getAccessGroupsFromCall,
      _: constant(Expressions.resume(emptyArray)),
    })
);

export const findAccessGroupsInComponentMap = weakMemo(
  (componentMap: Component.ComponentMap): Set<string> => {
    return new Set(
      findInComponentMapByComponentName(
        componentMap,
        qualify(Plugins.name('__SitePlugin'), Component.name('page'))
      )
    );
  }
);

export const USYS_FIELD_PATH: ExpressionPath = [
  {in: 'Record', at: 'users'},
  {in: 'Record', at: 'field'},
];

export const USYS_CONTEXT_PATH: ExpressionPath = [
  {in: 'Record', at: 'users'},
  {in: 'Record', at: 'context'},
];
